function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

document.addEventListener('click', (event) => {
  const overlay = event.target.closest('[data-mobile-menu-overlay]');
  if (overlay) {
    event.preventDefault();
    const drawer = overlay.closest('header-drawer');
    const summary = drawer?.querySelector('#Details-menu-drawer-container > summary');
    if (drawer && summary && typeof drawer.closeMenuDrawer === 'function') {
      drawer.closeMenuDrawer(event, summary);
    }
    return;
  }

  const viewAllBlog = event.target.closest('[data-mobile-menu-view-all-blog]');
  if (viewAllBlog) {
    const href = viewAllBlog.getAttribute('href') || '';
    if (href === '#blog' || href.endsWith('#blog')) {
      event.preventDefault();
      window.location.assign(
        viewAllBlog.dataset.blogUrl ||
          viewAllBlog.closest('header-drawer')?.dataset.mobileMenuBlogUrl ||
          '/blogs/podcast'
      );
      return;
    }
  }

  const closeDrawerButton = event.target.closest('[data-mobile-menu-close]');
  if (closeDrawerButton) {
    event.preventDefault();
    const drawer = closeDrawerButton.closest('header-drawer');
    const summary = drawer?.querySelector('#Details-menu-drawer-container > summary');
    if (drawer && summary && typeof drawer.closeMenuDrawer === 'function') {
      drawer.closeMenuDrawer(event, summary);
    }
    return;
  }

  const openProductButton = event.target.closest('[data-mobile-menu-product-open]');
  if (openProductButton) {
    event.preventDefault();
    const drawer = openProductButton.closest('header-drawer');
    const templateId = openProductButton.dataset.productTemplate;
    const template = (drawer && drawer.querySelector(`#${templateId}`)) || document.getElementById(templateId);
    const sheet =
      (drawer && drawer.querySelector(':scope > [data-mobile-menu-product-sheet]')) ||
      document.querySelector('[data-mobile-menu-product-sheet]');
    const content = sheet?.querySelector('[data-mobile-menu-product-content]');
    if (!template || !sheet || !content) return;

    content.innerHTML = template.innerHTML;
    sheet.hidden = false;
    sheet.classList.add('is-active');
    document.body.classList.add('overflow-hidden-tablet');

    const purchaseSelect = content.querySelector('[data-mobile-menu-product-select]');
    if (purchaseSelect) {
      purchaseSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }
    return;
  }

  const closeProductButton = event.target.closest('[data-mobile-menu-product-close]');
  if (closeProductButton) {
    event.preventDefault();
    const sheet = closeProductButton.closest('[data-mobile-menu-product-sheet]');
    const content = sheet?.querySelector('[data-mobile-menu-product-content]');
    if (!sheet) return;

    sheet.classList.remove('is-active');
    sheet.hidden = true;
    if (content) content.innerHTML = '';
    document.body.classList.remove('overflow-hidden-tablet');
    return;
  }

  const packAddButton = event.target.closest('[data-mega-menu-pack-add]');
  if (packAddButton) {
    event.preventDefault();
    if (packAddButton.disabled) return;

    const variantIds = (packAddButton.dataset.variantIds || '')
      .split(',')
      .map((id) => Number(id.trim()))
      .filter((id) => id > 0);

    if (!variantIds.length) return;

    packAddButton.disabled = true;
    packAddButton.setAttribute('aria-disabled', 'true');

    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const payload = {
      items: variantIds.map((id) => ({ id, quantity: 1 })),
    };

    if (cart) {
      payload.sections = cart.getSectionsToRender().map((section) => section.id).join(',');
      payload.sections_url = window.location.pathname;
      if (typeof cart.setActiveElement === 'function') {
        cart.setActiveElement(packAddButton);
      }
    }

    fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/javascript',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) throw new Error(response.description || 'cart_add_failed');

        if (cart && response.sections) {
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          cart.renderContents(response);
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'header-menu-pack',
            cartData: response,
          });
        }

        if (!cart || !response.sections) {
          window.location.href = (window.routes && window.routes.cart_url) || '/cart';
        }
      })
      .catch(() => {
        window.location.href = (window.routes && window.routes.cart_url) || '/cart';
      })
      .finally(() => {
        packAddButton.disabled = false;
        packAddButton.removeAttribute('aria-disabled');
      });
    return;
  }

  const quantityMinus = event.target.closest('[data-mobile-menu-quantity-minus]');
  const quantityPlus = event.target.closest('[data-mobile-menu-quantity-plus]');
  if (quantityMinus || quantityPlus) {
    event.preventDefault();
    const quantity = event.target.closest('[data-mobile-menu-quantity]')?.querySelector('input[name="quantity"]');
    if (!quantity) return;

    const currentValue = Number(quantity.value) || 1;
    quantity.value = quantityPlus ? currentValue + 1 : Math.max(1, currentValue - 1);
  }
});

function setMegaMenuPackBanner(panel, handle, productTitle = '', fallbackToDefaultHeading = true) {
  if (!panel) return;

  const target = handle
    ? panel.querySelector(`[data-mega-menu-pack-banner][data-product-handle="${CSS.escape(handle)}"]`)
    : null;

  panel.querySelectorAll('[data-mega-menu-pack-banner]').forEach((banner) => {
    banner.hidden = banner !== target;
  });

  const relatedTarget = handle
    ? panel.querySelector(`[data-mega-menu-related][data-product-handle="${CSS.escape(handle)}"]`)
    : null;

  panel.querySelectorAll('[data-mega-menu-related]').forEach((grid) => {
    grid.hidden = grid !== relatedTarget;
  });

  panel.querySelectorAll('[data-mega-menu-pack-trigger]').forEach((trigger) => {
    trigger.classList.toggle(
      'is-pack-active',
      Boolean(target || relatedTarget) && trigger.dataset.productHandle === handle
    );
  });

  const heading = panel.querySelector('[data-mega-menu-products-heading]');
  if (heading) {
    const defaultHeading = heading.dataset.defaultHeading || '';
    const headingText = heading.querySelector('[data-mega-menu-products-heading-text]');
    const headingPrefix = heading.querySelector('[data-mega-menu-products-heading-prefix]');
    const nextText = productTitle || (fallbackToDefaultHeading ? defaultHeading : '');
    const hasDynamicProduct = Boolean(productTitle);

    if (headingText) {
      headingText.textContent = nextText;
    } else {
      heading.textContent = nextText;
    }

    if (headingPrefix) {
      headingPrefix.hidden = !hasDynamicProduct;
    }
  }
}

function setMegaMenuBlogPosts(panel, blogKey = '') {
  if (!panel || !panel.classList.contains('mega-menu__blog')) return;

  const target = blogKey
    ? panel.querySelector(`[data-mega-menu-blog][data-blog-key="${CSS.escape(blogKey)}"]`)
    : null;

  panel.querySelectorAll('[data-mega-menu-blog]').forEach((grid) => {
    grid.hidden = Boolean(target) ? grid !== target : grid.dataset.blogKey !== '1';
  });

  panel.querySelectorAll('[data-mega-menu-blog-trigger]').forEach((trigger) => {
    trigger.classList.toggle('is-pack-active', Boolean(target) && trigger.dataset.blogKey === blogKey);
  });

  const viewAll = panel.querySelector('[data-mega-menu-blog-view-all]');
  if (viewAll) {
    const activeTrigger = blogKey
      ? panel.querySelector(`[data-mega-menu-blog-trigger][data-blog-key="${CSS.escape(blogKey)}"]`)
      : panel.querySelector('[data-mega-menu-blog-trigger][data-blog-key="1"]');
    const nextUrl = activeTrigger?.dataset.blogUrl || viewAll.dataset.defaultBlogUrl || viewAll.getAttribute('href');
    if (nextUrl) viewAll.setAttribute('href', nextUrl);
  }
}

document.addEventListener('toggle', (event) => {
  const details = event.target;
  if (!(details instanceof HTMLDetailsElement)) return;
  if (!details.classList.contains('mega-menu--products')) return;

  const panel = details.querySelector('.mega-menu__products');
  if (!panel) return;

  if (!details.open) {
    setMegaMenuPackBanner(panel, '');
    setMegaMenuBlogPosts(panel, '');
    return;
  }

  const firstTrigger = panel.querySelector('[data-mega-menu-pack-trigger]');
  if (firstTrigger?.dataset.productHandle) {
    setMegaMenuPackBanner(
      panel,
      firstTrigger.dataset.productHandle,
      firstTrigger.dataset.productTitle || ''
    );
  }

  const firstBlogTrigger = panel.querySelector('[data-mega-menu-blog-trigger]');
  if (firstBlogTrigger?.dataset.blogKey) {
    setMegaMenuBlogPosts(panel, firstBlogTrigger.dataset.blogKey);
  }
}, true);

document.addEventListener('mouseover', (event) => {
  const panel = event.target.closest('.mega-menu__products');
  if (!panel) return;

  // Keep current banner/related while moving toward them / interacting with them.
  if (
    event.target.closest(
      '[data-mega-menu-pack-slot], [data-mega-menu-pack-banner], [data-mega-menu-related-slot], [data-mega-menu-related], [data-mega-menu-blog-slot], [data-mega-menu-blog]'
    )
  ) {
    return;
  }

  const navLink = event.target.closest('.mega-menu__products-link, .mega-menu__products-view-all');
  if (!navLink || !panel.contains(navLink)) return;

  if (panel.classList.contains('mega-menu__blog')) {
    const blogKey = navLink.dataset.blogKey || '';
    if (blogKey || navLink.classList.contains('mega-menu__products-view-all')) {
      setMegaMenuBlogPosts(panel, blogKey || '1');
    }
    return;
  }

  const isViewAllLink = navLink.classList.contains('mega-menu__products-view-all');
  const handle = navLink.dataset.productHandle || '';
  const productTitle = navLink.dataset.productTitle || '';
  setMegaMenuPackBanner(panel, handle, productTitle, !isViewAllLink);
});

document.addEventListener('focusin', (event) => {
  const panel = event.target.closest('.mega-menu__products');
  if (!panel) return;

  const navLink = event.target.closest('.mega-menu__products-link, .mega-menu__products-view-all');
  if (!navLink || !panel.contains(navLink)) return;

  if (panel.classList.contains('mega-menu__blog')) {
    const blogKey = navLink.dataset.blogKey || '';
    if (blogKey || navLink.classList.contains('mega-menu__products-view-all')) {
      setMegaMenuBlogPosts(panel, blogKey || '1');
    }
    return;
  }

  const isViewAllLink = navLink.classList.contains('mega-menu__products-view-all');
  const handle = navLink.dataset.productHandle || '';
  const productTitle = navLink.dataset.productTitle || '';
  setMegaMenuPackBanner(panel, handle, productTitle, !isViewAllLink);
});

document.addEventListener('change', (event) => {
  const select = event.target.closest('[data-mobile-menu-product-select]');
  if (select) {
    const option = select.selectedOptions[0];
    const form = select.closest('[data-mobile-menu-product-form]');
    const price = form?.querySelector('[data-mobile-menu-modal-price]');
    const compare = form?.querySelector('[data-mobile-menu-modal-compare]');
    const savings = form?.querySelector('[data-mobile-menu-modal-savings]');

    if (price && option.dataset.price) price.textContent = option.dataset.price;

    if (compare) {
      compare.textContent = option.dataset.compare || '';
      compare.hidden = !option.dataset.compare;
    }

    if (savings) {
      savings.textContent = option.dataset.savings || '';
      savings.hidden = !option.dataset.savings;
    }
  }
});

document.addEventListener('click', (event) => {
  if (event.target.closest('form[data-open-cart-drawer="true"]')) {
    event.stopPropagation();
  }
}, true);

document.addEventListener('submit', (event) => {
  const form = event.target.closest?.('form[data-open-cart-drawer="true"]') ||
    (event.target.matches?.('form[data-open-cart-drawer="true"]') ? event.target : null);
  if (!form) return;

  event.preventDefault();
  event.stopPropagation();

  const button = form.querySelector('[type="submit"]');
  if (!button || button.disabled) return;

  const variantInput = form.querySelector('[name="id"]');
  const quantityInput = form.querySelector('[name="quantity"]');
  if (!variantInput) return;

  button.disabled = true;
  button.setAttribute('aria-disabled', 'true');

  const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  const payload = {
    items: [
      {
        id: Number(variantInput.value),
        quantity: quantityInput ? Number(quantityInput.value) || 1 : 1,
      },
    ],
  };

  if (cart) {
    payload.sections = cart.getSectionsToRender().map((section) => section.id).join(',');
    payload.sections_url = window.location.pathname;
    if (typeof cart.setActiveElement === 'function') {
      cart.setActiveElement(button);
    }
  }

  fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/javascript',
    },
    body: JSON.stringify(payload),
  })
    .then((response) => response.json())
    .then((response) => {
      if (response.status) throw new Error(response.description || 'cart_add_failed');

      if (cart && response.sections) {
        if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
        cart.renderContents(response);

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'card-add-to-cart',
            cartData: response,
          });
        }
        return;
      }

      window.location.href = (window.routes && window.routes.cart_url) || '/cart';
    })
    .catch(() => {
      window.location.href = (window.routes && window.routes.cart_url) || '/cart';
    })
    .finally(() => {
      button.disabled = false;
      button.removeAttribute('aria-disabled');
    });
}, true);

document.addEventListener('submit', (event) => {
  const form = event.target.closest('[data-mobile-menu-product-form]');
  if (!form) return;

  event.preventDefault();
  const submit = form.querySelector('[type="submit"]');
  const select = form.querySelector('[data-mobile-menu-product-select]');
  const quantity = form.querySelector('input[name="quantity"]');
  const option = select?.selectedOptions?.[0];
  if (!submit || !option || submit.disabled) return;

  submit.disabled = true;
  submit.setAttribute('aria-disabled', 'true');

  const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  const item = {
    id: Number(option.dataset.variantId || option.value),
    quantity: quantity ? Number(quantity.value) || 1 : 1,
  };

  if (option.dataset.sellingPlan) {
    item.selling_plan = Number(option.dataset.sellingPlan);
  }

  const payload = { items: [item] };
  if (cart) {
    payload.sections = cart.getSectionsToRender().map((section) => section.id).join(',');
    payload.sections_url = window.location.pathname;
    if (typeof cart.setActiveElement === 'function') {
      cart.setActiveElement(submit);
    }
  }

  fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/javascript',
    },
    body: JSON.stringify(payload),
  })
    .then((response) => response.json())
    .then((response) => {
      if (response.status) throw new Error(response.description || 'cart_add_failed');

      const sheet = form.closest('[data-mobile-menu-product-sheet]') ||
        document.querySelector('[data-mobile-menu-product-sheet].is-active');
      const sheetContent = sheet?.querySelector('[data-mobile-menu-product-content]');
      if (sheet) {
        sheet.classList.remove('is-active');
        sheet.hidden = true;
      }
      if (sheetContent) sheetContent.innerHTML = '';
      document.body.classList.remove('overflow-hidden-tablet');

      /* para volver a la anterior version simplmente cambia el orden de este codigo*/
      const drawer = document.querySelector('header-drawer');
      const firstCloseButton = drawer?.querySelector('[data-mobile-menu-close]');
      console.log('Primer [data-mobile-menu-close]:', firstCloseButton);
      if (cart && response.sections) {
        if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
        cart.renderContents(response);

        if (firstCloseButton) {
          firstCloseButton.click();
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'mobile-menu-v2',
            cartData: response,
          });
        }
        return;
      }
      const drawerSummary = drawer?.querySelector('#Details-menu-drawer-container > summary');
      if (drawer && drawerSummary && typeof drawer.closeMenuDrawer === 'function') {
        drawer.closeMenuDrawer(null, drawerSummary);
      }
      
      /*aqui muere el cambio de orden*/

      window.location.href = (window.routes && window.routes.cart_url) || '/cart';
    })
    .catch((error) => {
      console.error('Mobile menu cart error:', error);
    })
    .finally(() => {
      submit.disabled = false;
      submit.removeAttribute('aria-disabled');
    });
});

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class ProductGallery extends HTMLElement {
  connectedCallback() {
    const slider = this.querySelector('#slider-wrapper');
    const dots = Array.from(this.querySelectorAll('.slider-dot'));
    const thumbnails = Array.from(this.querySelectorAll('.thumbnail'));
    const slides = Array.from(this.querySelectorAll('.slider-slide'));
    const mainSlides = Array.from(this.querySelectorAll('.gallery-main-slide'));
    const mainImage = this.querySelector('#main-image');
    const legacySlideImages = Array.from(this.querySelectorAll('.slider-slide img'));
    const usesMainSlides = mainSlides.length > 0;
    const prevButtons = Array.from(this.querySelectorAll('.product-gallery-arrow--left'));
    const nextButtons = Array.from(this.querySelectorAll('.product-gallery-arrow--right'));
    let touchStartX = 0;
    let touchEndX = 0;
    let currentIndex = 0;
    const totalSlides = slides.length || mainSlides.length;

    if (!totalSlides) return;

    this.initVideoControls();
    this.initGalleryLightbox();

    const pauseAllVideos = () => {
      this.querySelectorAll('.gallery-video__player').forEach((video) => {
        if (!video.paused) video.pause();
      });

      this.querySelectorAll('.gallery-video').forEach((wrapper) => {
        if (wrapper.classList.contains('gallery-video--external') && wrapper.classList.contains('is-playing')) {
          this.pauseExternalVideo(wrapper);
          return;
        }
        wrapper.classList.remove('is-playing');
      });
    };

    const updateMainMedia = (index) => {
      if (usesMainSlides) {
        mainSlides.forEach((slide, slideIndex) => {
          slide.classList.toggle('is-active', slideIndex === index);
        });
        return;
      }

      if (!mainImage) return;

      const slideImage = legacySlideImages[index];
      const thumbnail = thumbnails[index];
      const nextSrc = thumbnail?.getAttribute('data-full-img') || slideImage?.currentSrc || slideImage?.src;
      const imageLink = thumbnail?.getAttribute('data-img-url');

      if (nextSrc) {
        mainImage.src = nextSrc;
      }

      mainImage.style.animation = 'none';
      void mainImage.offsetWidth;
      mainImage.style.animation = null;

      if (mainImage.parentElement?.tagName === 'A') {
        if (imageLink) {
          mainImage.parentElement.href = imageLink;
          return;
        }
        mainImage.parentElement.removeAttribute('href');
      }
    };

    const mobileThumbStrip = this.querySelector('.slider-dots--mobile.slider-thumbnails');
    const mobileMediaQuery = window.matchMedia('(max-width: 989px)');

    const scrollActiveMobileThumb = (index) => {
      if (!mobileThumbStrip) return;
      const activeThumb = mobileThumbStrip.querySelector(`.slider-thumbnail[data-index="${index}"]`);
      activeThumb?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    };

    const updateThumbnails = (index) => {
      thumbnails.forEach((thumb, thumbIndex) => {
        thumb.classList.toggle('is-active', thumbIndex === index);
      });
    };

    const updateSlider = (index) => {
      const safeIndex = (index + totalSlides) % totalSlides;

      pauseAllVideos();

      if (mobileMediaQuery.matches) {
        slides.forEach((slide, slideIndex) => {
          slide.classList.toggle('is-active', slideIndex === safeIndex);
        });
        if (slider) slider.style.transform = '';
      } else if (slider) {
        const offset = -safeIndex * 100;
        slider.style.transform = `translateX(${offset}%)`;
      }

      dots.forEach((dot) => {
        const dotIndex = Number(dot.dataset.index);
        dot.classList.toggle('active', dotIndex === safeIndex);
      });

      updateMainMedia(safeIndex);
      updateThumbnails(safeIndex);
      scrollActiveMobileThumb(safeIndex);
      currentIndex = safeIndex;
    };

    if (slider) {
      slider.addEventListener('touchstart', (e) => {
        touchStartX = e.touches[0].clientX;
      });

      slider.addEventListener('touchend', (e) => {
        touchEndX = e.changedTouches[0].clientX;
        const diff = touchStartX - touchEndX;
        const threshold = 50;
        if (Math.abs(diff) > threshold) {
          updateSlider(diff > 0 ? currentIndex + 1 : currentIndex - 1);
        }
      });
    }

    dots.forEach((dot) => {
      dot.addEventListener('click', () => updateSlider(Number(dot.dataset.index)));
    });

    thumbnails.forEach((thumb, index) => {
      thumb.addEventListener('click', () => updateSlider(index));
    });

    prevButtons.forEach((button) => {
      button.addEventListener('click', () => updateSlider(currentIndex - 1));
    });
    nextButtons.forEach((button) => {
      button.addEventListener('click', () => updateSlider(currentIndex + 1));
    });

    updateSlider(0);
  }

  initVideoControls() {
    this.querySelectorAll('.gallery-video:not(.gallery-video--external)').forEach((wrapper) => {
      const video = wrapper.querySelector('.gallery-video__player');
      const toggle = wrapper.querySelector('.gallery-video__toggle');
      if (!video || !toggle) return;

      const setPlaying = (playing) => {
        wrapper.classList.toggle('is-playing', playing);
        toggle.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
      };

      toggle.addEventListener('click', (event) => {
        event.stopPropagation();
        if (video.paused) {
          this.pauseAllGalleryVideos();
          video.play();
        } else {
          video.pause();
        }
      });

      video.addEventListener('play', () => setPlaying(true));
      video.addEventListener('pause', () => setPlaying(false));
      video.addEventListener('ended', () => setPlaying(false));
    });

    this.querySelectorAll('.gallery-video--external').forEach((wrapper) => {
      const toggle = wrapper.querySelector('.gallery-video__toggle');
      const poster = wrapper.querySelector('.gallery-video__poster');
      const iframeWrap = wrapper.querySelector('.gallery-video__iframe-wrap');
      const videoSrc = toggle?.dataset.videoSrc;
      if (!toggle || !poster || !iframeWrap || !videoSrc) return;

      toggle.addEventListener('click', (event) => {
        event.stopPropagation();
        if (wrapper.classList.contains('is-playing')) {
          this.pauseExternalVideo(wrapper);
          return;
        }

        this.pauseAllGalleryVideos();

        const iframe = document.createElement('iframe');
        iframe.src = videoSrc;
        iframe.className = 'gallery-video__iframe';
        iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
        iframe.setAttribute('allowfullscreen', '');
        iframe.setAttribute('title', 'Product video');

        iframeWrap.innerHTML = '';
        iframeWrap.appendChild(iframe);
        iframeWrap.hidden = false;
        poster.hidden = true;
        wrapper.classList.add('is-playing');
        toggle.setAttribute('aria-label', 'Pause video');
      });
    });
  }

  initGalleryLightbox() {
    let lightbox = this._galleryLightbox || this.querySelector('.gallery-lightbox');
    if (!lightbox) return;

    if (!lightbox.dataset.movedToBody) {
      lightbox.dataset.movedToBody = 'true';
      document.body.appendChild(lightbox);
      this._galleryLightbox = lightbox;
    }

    const content = lightbox.querySelector('.gallery-lightbox__content');
    const closeButton = lightbox.querySelector('.gallery-lightbox__close');
    const backdrop = lightbox.querySelector('.gallery-lightbox__backdrop');
    let lastOpener = null;

    const closeLightbox = () => {
      content.innerHTML = '';
      lightbox.hidden = true;
      lightbox.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', this._onGalleryLightboxKeydown);
      lastOpener?.focus();
      lastOpener = null;
    };

    this._onGalleryLightboxKeydown = (event) => {
      if (event.key === 'Escape') closeLightbox();
    };

    const openLightbox = (trigger) => {
      const mediaType = trigger.dataset.mediaType || 'image';
      lastOpener = trigger;

      this.pauseAllGalleryVideos();
      content.innerHTML = '';

      if (mediaType === 'video') {
        const slideVideo = trigger.closest('.gallery-media-item')?.querySelector('video');
        if (slideVideo) {
          const video = slideVideo.cloneNode(true);
          video.className = 'gallery-lightbox__video';
          video.controls = true;
          video.autoplay = true;
          video.removeAttribute('controlslist');
          content.appendChild(video);
          video.play().catch(() => {});
        } else if (trigger.dataset.videoSrc) {
          const video = document.createElement('video');
          video.className = 'gallery-lightbox__video';
          video.controls = true;
          video.playsInline = true;
          video.autoplay = true;
          const source = document.createElement('source');
          source.src = trigger.dataset.videoSrc;
          if (trigger.dataset.videoMime) source.type = trigger.dataset.videoMime;
          video.appendChild(source);
          content.appendChild(video);
        }
      } else if (mediaType === 'external_video' && trigger.dataset.videoSrc) {
        const iframe = document.createElement('iframe');
        iframe.className = 'gallery-lightbox__iframe';
        iframe.src = trigger.dataset.videoSrc;
        iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
        iframe.setAttribute('allowfullscreen', '');
        iframe.setAttribute('title', 'Product video');
        content.appendChild(iframe);
      } else {
        const img = document.createElement('img');
        img.className = 'gallery-lightbox__image';
        img.src = trigger.dataset.imageSrc;
        img.alt = trigger.dataset.imageAlt || '';
        content.appendChild(img);
      }

      lightbox.hidden = false;
      lightbox.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', this._onGalleryLightboxKeydown);
      closeButton?.focus();
    };

    this.querySelectorAll('.gallery-zoom-trigger').forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        openLightbox(trigger);
      });
    });

    closeButton?.addEventListener('click', closeLightbox);
    backdrop?.addEventListener('click', closeLightbox);
  }

  pauseAllGalleryVideos() {
    this.querySelectorAll('.gallery-video__player').forEach((video) => {
      if (!video.paused) video.pause();
    });

    this.querySelectorAll('.gallery-video--external.is-playing').forEach((wrapper) => {
      this.pauseExternalVideo(wrapper);
    });

    this.querySelectorAll('.gallery-video:not(.gallery-video--external)').forEach((wrapper) => {
      wrapper.classList.remove('is-playing');
    });
  }

  pauseExternalVideo(wrapper) {
    const toggle = wrapper.querySelector('.gallery-video__toggle');
    const poster = wrapper.querySelector('.gallery-video__poster');
    const iframeWrap = wrapper.querySelector('.gallery-video__iframe-wrap');
    if (!poster || !iframeWrap) return;

    iframeWrap.innerHTML = '';
    iframeWrap.hidden = true;
    poster.hidden = false;
    wrapper.classList.remove('is-playing');
    toggle?.setAttribute('aria-label', 'Play video');
  }
}

customElements.define('product-gallery', ProductGallery);
class SliderWithDots extends HTMLElement {
  connectedCallback() {
    const slider = this.querySelector('#slider-wrapper');
    const dots = this.querySelectorAll('.slider-dot');
    let touchStartX = 0;
    let touchEndX = 0;
    let currentIndex = 0;
    const totalSlides = this.querySelectorAll('.slider-slide').length;

    const updateSlider = (index) => {
      const offset = -index * 100;
      slider.style.transform = `translateX(${offset}%)`;
      dots.forEach((dot) => dot.classList.remove('active'));
      dots[index].classList.add('active');
      currentIndex = index;
    };

    slider.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
    });

    slider.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].clientX;
      const diff = touchStartX - touchEndX;
      const threshold = 50;
      if (Math.abs(diff) > threshold) {
        if (diff > 0 && currentIndex < totalSlides - 1) updateSlider(currentIndex + 1);
        if (diff < 0 && currentIndex > 0) updateSlider(currentIndex - 1);
      }
    });

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => updateSlider(index));
    });
  }
}

customElements.define('slider-with-dots', SliderWithDots);


class Popup extends HTMLElement {
  connectedCallback() {
    const isPopupEnabled = this.dataset.enabled === "true" || this.dataset.enabled === "1";
    if (!isPopupEnabled) return;

    const overlay = this.querySelector("#popupOverlay2Col");
    const closeBtn = this.querySelector("#closePopup2Col");
    const noThanksLink = this.querySelector("#noThanksLink2Col");
    const popupDelay = parseInt(this.dataset.delay, 10) || 0;

    //Cookie functions
    const setPopupCookie = (id, durationDays) => {
      const cookieName = `popUpClosed-id--${id}`;
      const date = new Date();
      date.setTime(date.getTime() + durationDays * 24 * 60 * 60 * 1000);
      const expires = "expires=" + date.toUTCString();
      document.cookie = `${cookieName}=true; ${expires}; path=/; SameSite=Lax`;
    };

    const getPopupCookie = (id) => {
      const cookieName = `popUpClosed-id--${id}=`;
      const cookies = document.cookie.split(";").map(c => c.trim());
      return cookies.some(c => c.startsWith(cookieName));
    }

    if (getPopupCookie(this.dataset.popupId)) return;

    // Close handle and events
    const closePopup = () => {
      if (overlay) overlay.style.display = "none";
      setPopupCookie(this.dataset.popupId, 7);
    };

    if (closeBtn) closeBtn.addEventListener("click", closePopup);
    if (noThanksLink) noThanksLink.addEventListener("click", closePopup);

    if (overlay) {
      overlay.addEventListener("click", (event) => {
        if (event.target === overlay) {
          closePopup();
        }
      });
    };

    // Show popup after X seconds
    setTimeout(() => {
      if (overlay) {
        overlay.style.display = "flex";
      }
    }, popupDelay * 1000);
  }
}

customElements.define("pop-up", Popup);


function watchViewportPosition(element) {
  if (!element) return function () {};

  function checkPosition() {
    const topOnScrollMobile =
      element.dataset &&
      element.dataset.topOnScroll === "true" &&
      (window.pageYOffset || document.documentElement.scrollTop || 0) > 0;

    if (topOnScrollMobile) {
      element.classList.add("top-of-viewport");
      element.classList.remove("bottom-of-viewport", "in-viewport");
      return;
    }

    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    const isAboveViewport = rect.bottom <= 0;
    const isBelowViewport = rect.top >= viewportHeight;
    const isVisible = rect.top < viewportHeight && rect.bottom > 0;

    if (isAboveViewport) {
      element.classList.add("top-of-viewport");
      element.classList.remove("bottom-of-viewport", "in-viewport");
    } else if (isBelowViewport) {
      element.classList.add("bottom-of-viewport");
      element.classList.remove("top-of-viewport", "in-viewport");
    } else if (isVisible) {
      element.classList.add("in-viewport");
      element.classList.remove("top-of-viewport", "bottom-of-viewport");
    }
  }

  checkPosition();

  window.addEventListener("scroll", checkPosition);
  window.addEventListener("resize", checkPosition);

  return function () {
    window.removeEventListener("scroll", checkPosition);
    window.removeEventListener("resize", checkPosition);
  };
}

// Ocultar announcement bar VIP al hacer scroll
(function() {
  var announcementBar = null;
  var scrollThreshold = 50; // Ocultar después de 50px de scroll
  var ticking = false;
  
  function isVipRoomActive() {
    var body = document.body;
    if (!body) return false;
    var adsguncode = body.getAttribute('data-adsguncode');
    return adsguncode && adsguncode.includes('VIPROOM');
  }
  
  function handleScroll() {
    // Verificar si el elemento existe (puede no existir si no está visible por CSS)
    if (!announcementBar) {
      announcementBar = document.getElementById('announcement-bar-vip');
    }
    
    if (!announcementBar || !isVipRoomActive()) {
      ticking = false;
      return;
    }
    
    var currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
    
    if (currentScrollTop > scrollThreshold) {
      announcementBar.style.display = 'none';
    } else {
      // Mostrar solo si VIPROOM está activo
      announcementBar.style.display = 'block';
    }
    
    ticking = false;
  }
  
  function initAnnouncementBarScroll() {
    announcementBar = document.getElementById('announcement-bar-vip');
    
    if (!announcementBar) {
      // Intentar de nuevo después de un delay por si el elemento se carga después
      setTimeout(initAnnouncementBarScroll, 500);
      return;
    }
    
    window.addEventListener('scroll', function() {
      if (!ticking) {
        window.requestAnimationFrame(handleScroll);
        ticking = true;
      }
    }, { passive: true });
    
    // Ejecutar una vez al inicio para verificar el estado inicial
    handleScroll();
  }
  
  // Ejecutar cuando el DOM esté listo
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAnnouncementBarScroll);
  } else {
    initAnnouncementBarScroll();
  }
})();

class DoublePopUp extends HTMLElement {
  connectedCallback() {
    const isPopupEnabled =
      this.dataset.enabled === "true" || this.dataset.enabled === "1";
    if (!isPopupEnabled) return;

    const overlay = this.querySelector("#doublePopupOverlay");
    const closeBtn = this.querySelector("#closeDoublePopup");
    const firstImage = this.querySelector("#FirstPopup");
    const secondImage = this.querySelector("#SecondPopup");
    const trigger = document.querySelector(`#${this.dataset.triggerId}`);
    const popupDelay = parseInt(this.dataset.delay, 10) || 0;
    const popupId = this.dataset.popupId;
    const formId = this.dataset.formId;

    /* -------------------- Cookie utils -------------------- */
    const setPopupCookie = (id, days) => {
      const cookieName = `doublePopUpClosed-id--${id}`;
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      document.cookie = `${cookieName}=true; expires=${date.toUTCString()}; path=/; SameSite=Lax`;
    };

    const getPopupCookie = (id) => {
      const cookieName = `doublePopUpClosed-id--${id}=`;
      return document.cookie
        .split(";")
        .map((c) => c.trim())
        .some((c) => c.startsWith(cookieName));
    };

    const isFormSubmitted = (formId) => {
      const key = `${formId}-dismissed-after-submit`;
      return localStorage.getItem(key) !== null;
    };

    // If form was submitted previously → prevent auto-popup forever
    if (isFormSubmitted(formId)) {
      setPopupCookie(popupId, 99999);
    }

    /* -------------------- Popup Control Functions -------------------- */

    const openPopup = (showSecond = false) => {
      if (!overlay || !firstImage || !secondImage) return;

      overlay.style.display = "flex";
      firstImage.style.display = showSecond ? "none" : "flex";
      secondImage.style.display = showSecond ? "flex" : "none";
    };

    const closePopup = () => {
      if (overlay) overlay.style.display = "none";
      setPopupCookie(popupId, 99999);
    };

    /* -------------------- Event Listeners -------------------- */
    closeBtn?.addEventListener("click", closePopup);

    overlay?.addEventListener("click", (e) => {
      if (e.target === overlay) closePopup();
    });

    // Manual trigger: always open second image (ignore cookie)
    if (trigger) {
      trigger.addEventListener("click", () => {
        openPopup(true)
      });
    }

    // Click on first image → show second
    if (firstImage && secondImage) {
      secondImage.style.display = "none";
      firstImage.addEventListener("click", () => {
        firstImage.style.display = "none";
        secondImage.style.display = "flex";
      });
    }

    /* -------------------- Automatic Behavior -------------------- */
    if (!getPopupCookie(popupId)) {
      // No cookie → show popup automatically after delay
      setTimeout(() => openPopup(false), popupDelay * 1000);
    }
  }
}

customElements.define("double-pop-up", DoublePopUp);

